import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Landmark, Smartphone, FileText, FileSpreadsheet, ShieldCheck, RotateCcw, X, Loader2, UserPlus, Trash2, Lock, Mail } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { WizardProgress, WizardFooter, Button, Pill, Card, CardBody, Field, Input, Select } from "@/design/components";
import { ImpactLedgerLogo } from "@/shell/ImpactLedgerLogo";
import { useActiveWorkspace, useSeed } from "@/domain/hooks";
import {
  usePeekInvite,
  useStartOnboarding,
  useCompleteStep,
  useCompleteOnboarding,
  useForwardInvite,
  useSetPassword,
} from "@/domain/useOnboarding";
import { api, type OnboardingInvite } from "@/domain/api";
import { useAuthStore } from "@/domain/authStore";
import { ReporterWizard } from "./ReporterWizard";
import { StatementUpload, type StatementImportSummary } from "./StatementUpload";

// UI steps and the backend catalogue step each one commits (null = UI-only, no
// backend call). The router auto-skips `kyc` (before bank) and
// `subcontractor_invites` (during finalise), so those never appear here. Order
// follows the backend machine: project_acceptance → compliance → team_invites.
// See .claude/implementation-plan.md (resolved 2026-07-01).
const STEPS: { key: string; label: string; backend: string | null }[] = [
  { key: "welcome", label: "Welcome", backend: "welcome" },
  { key: "profile", label: "Account", backend: null },
  { key: "organisation", label: "Organisation", backend: "org_details" },
  { key: "bank", label: "Bank", backend: "bank_verification" },
  { key: "projects", label: "Projects", backend: "project_acceptance" },
  { key: "declaration", label: "Declaration", backend: "compliance_acknowledgement" },
  { key: "team", label: "Team", backend: "team_invites" },
  { key: "gate", label: "Approval gate", backend: null },
  { key: "activation", label: "Activation", backend: null },
];

const GATE_INDEX = STEPS.findIndex((s) => s.key === "gate");
const ACTIVATION_INDEX = STEPS.findIndex((s) => s.key === "activation");

// An onboarding invite is scoped to a SINGLE project: the magic_link carries one
// projectId and the project-acceptance step commits the prime's membership to
// just that project. So the Welcome preview and the Projects step both show one
// project (they must match — a two-vs-one split was the originally reported bug).
// Multi-project ownership is a post-onboarding concern (the §9.5 dashboard modal).
const ASSIGNED_PROJECT_COUNT = 1;

// The project the invite is actually for, named by the backend peek.
//
// Both project surfaces used to slice `seed.projects` instead — i.e. whichever
// project happened to be FIRST in whatever workspace was active in this
// browser. So an invite to project A rendered as project B (reported live).
// The invite is the only source of truth here; the seed slice survives purely
// as a fallback for an invite that carries no project at all.
type AssignedProject = {
  id: string;
  name: string;
  district: string | null;
  budgeted: number | null;
};

function assignedProjectFor(
  invite: OnboardingInvite | undefined,
  seedProjects: { id: string; name: string; district?: string | null; budgeted?: number | null }[],
): AssignedProject | null {
  if (invite?.projectName) {
    return {
      id: String(invite.projectSlug ?? invite.projectId ?? "invited-project"),
      name: invite.projectName,
      district: invite.projectDistrict,
      budgeted: invite.projectBudgeted,
    };
  }
  const fallback = seedProjects.slice(0, ASSIGNED_PROJECT_COUNT)[0];
  if (!fallback) return null;
  return {
    id: fallback.id,
    name: fallback.name,
    district: fallback.district ?? null,
    budgeted: fallback.budgeted ?? null,
  };
}

// Wizard tiers with a built UI. Prime (tier2) and new-subcontractor (tier3_sub_new)
// both run THIS wizard — tier-3 is "a lighter version of the prime wizard" per
// spec §9.3, differing only in the first step name and the team roles it can
// appoint. Reporter (tier4) is dispatched to its own component before the gate
// below. The returning-subcontractor tier is a v2 stub (spec §9.3) — the router
// never routes to it — so it correctly falls to the "coming soon" screen.
const SUPPORTED_WIZARDS = new Set(["tier2_prime", "tier3_sub_new", "tier4_reporter"]);

// A team member invited during onboarding. `role` is the cascade role key sent to
// the backend (team_invites → cascade.inviteStaffMember); `roleLabel` is display-only.
// The Team step collects these and each entry becomes a pending invite.
type TeamInvite = {
  name: string;
  email: string;
  role: string;
  roleLabel: string;
  /** Optional sub-project (of the onboarding project) to scope this member to. */
  subProjectId?: string;
  subProjectName?: string;
};

// Cascade staff roles the onboarding org can appoint on their project, per the
// zeus-cascade role pack: a prime_lead appoints prime_member (and sub_lead, via
// the separate subcontractor step) but NOT reporter; a sub_lead appoints
// sub_member and reporter. So the team step's options depend on the tier.
function staffRoleOptionsFor(wizardType: string | null): { key: string; label: string }[] {
  if (wizardType === "tier3_sub_new") {
    return [
      { key: "sub_member", label: "Team member" },
      { key: "reporter", label: "Reporter (field staff)" },
    ];
  }
  return [
    { key: "prime_member", label: "Project member" },
    { key: "sub_lead", label: "Sub-project lead" },
  ];
}

// A real email-format check. The previous `/.+@.+\..+/` accepted spaces and
// multiple @, so prose like "not an email" could slip through — this requires a
// single @ with no whitespace and a dotted domain.
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// Human label for the invited cascade role, shown read-only on the Account step.
function roleLabel(role: string | null | undefined): string {
  switch (role) {
    case "prime_lead":
      return "Project lead";
    case "sub_lead":
      return "Sub-project lead";
    case "prime_member":
      return "Project member";
    case "sub_member":
      return "Team member";
    case "reporter":
      return "Reporter (field staff)";
    default:
      return "Team member";
  }
}

// Minimum account password length (matches the backend guard).
const MIN_PASSWORD_LENGTH = 8;

// The fields we persist so "Save and finish later" returns the user to their typed work,
// not a blank form. Lifted to the wizard root and written to localStorage on every change.
type WizardData = {
  organisation: Record<string, string>;
  /** Set once the account password has been saved. The password itself is
   * NEVER persisted — only this completion flag. */
  profileComplete: boolean;
  bankMethod: string | null;
  /** Set once a statement CSV import succeeded (method "statement"). */
  statementImport: StatementImportSummary | null;
  projectActions: Record<string, string>;
  declarationAccepted: boolean;
  teamInvites: TeamInvite[];
};

const EMPTY_DATA: WizardData = {
  organisation: {
    legalName: "",
    tradingName: "",
    registrationId: "",
    taxId: "",
    country: "",
    primaryContact: "",
  },
  profileComplete: false,
  bankMethod: null,
  statementImport: null,
  projectActions: {},
  declarationAccepted: false,
  teamInvites: [],
};

// `furthest` is the highest step index the user has advanced past via Continue —
// i.e. the frontier of what the backend wizard machine has already committed.
// Steps below it are done on the backend and must NOT be re-committed on the way
// forward again (the machine rejects out-of-order commits).
type Persisted = { step: number; data: WizardData; sessionId: number | null; furthest: number };

export function OnboardingWizard() {
  const ws = useActiveWorkspace();
  const seed = useSeed();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const { inviteToken } = useParams();
  const storageKey = `il-onboarding-${inviteToken ?? "demo"}`;

  // Backend session + issued auth are the source of truth; localStorage is a
  // local draft cache so typed values survive a refresh mid-step.
  const peek = usePeekInvite(inviteToken);
  const { start, isPending: starting } = useStartOnboarding();
  const { completeStep, isPending: completingStep } = useCompleteStep();
  const { completeOnboarding, isPending: finalizing } = useCompleteOnboarding();

  // Save-and-resume across sessions, keyed on the invite link (P2.2). A fresh invite has
  // nothing stored; only a real prior save produces a resume landing.
  const [saved] = useState<Persisted | null>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return null;
      const parsed = JSON.parse(raw) as Partial<Persisted>;
      if (typeof parsed.step !== "number") return null;
      return {
        step: parsed.step,
        data: { ...EMPTY_DATA, ...(parsed.data ?? {}) },
        sessionId: parsed.sessionId ?? null,
        // Older saves have no `furthest`; the resumed step is a safe floor since
        // everything up to it was already committed to reach that point.
        furthest: parsed.furthest ?? parsed.step,
      };
    } catch {
      return null;
    }
  });

  // P0-5: a brand-new invite starts at step 0 (Welcome), never mid-flow with earlier
  // steps pre-ticked for work the user never did.
  const [step, setStep] = useState(saved?.step ?? 0);
  const [furthest, setFurthest] = useState(saved?.furthest ?? saved?.step ?? 0);
  const [data, setData] = useState<WizardData>(saved?.data ?? EMPTY_DATA);
  const [sessionId, setSessionId] = useState<number | null>(saved?.sessionId ?? null);
  const [landing, setLanding] = useState<string>("/workspace/uganda-fire-stations");
  const [resuming, setResuming] = useState(saved != null);
  // The specific unmet requirement for the current step, shown inline instead of a silent
  // disabled Continue (P0-11).
  const [blocker, setBlocker] = useState<string | null>(null);
  const [forwardOpen, setForwardOpen] = useState(false);

  const busy = starting || completingStep || finalizing;

  // The one project this invite assigns — from the invite itself, not the seed.
  // Drives the Welcome preview, the Projects step and its validation, so all
  // three name the same (correct) project.
  const assignedProject = assignedProjectFor(peek.data?.invite, seed.projects);
  const assignedProjectCount = assignedProject ? 1 : 0;

  // Tier parameters (prime tier2 vs new-subcontractor tier3). peek.data is
  // guaranteed present by the time the wizard renders / a handler fires.
  const wizardType = peek.data?.wizardType ?? null;
  const firstStepBackend = wizardType === "tier3_sub_new" ? "account" : "welcome";
  const staffRoleOptions = staffRoleOptionsFor(wizardType);

  function persist(
    nextStep: number,
    nextData: WizardData,
    sid: number | null = sessionId,
    far: number = furthest
  ) {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ step: nextStep, data: nextData, sessionId: sid, furthest: far })
      );
    } catch {
      /* ignore */
    }
  }

  function patch(p: Partial<WizardData>) {
    setData((d) => {
      const next = { ...d, ...p };
      persist(step, next);
      return next;
    });
  }

  function saveLater() {
    persist(step, data);
    toast.success("Saved. Your details and progress are kept; return via your invite link to continue.");
  }

  // P0-11: validate the current step. Returns the first unmet requirement, or null if the
  // step may be left. Continue surfaces this instead of silently doing nothing.
  function validateStep(i: number): string | null {
    const k = STEPS[i].key;
    if (k === "profile") {
      if (!data.profileComplete) return "Set your account password before continuing.";
    }
    if (k === "organisation") {
      const o = data.organisation;
      const missing = [
        !o.legalName.trim() && "legal name",
        !o.registrationId.trim() && "registration ID",
        !o.country.trim() && "country of incorporation",
        !o.primaryContact.trim() && "primary contact",
      ].filter(Boolean);
      if (missing.length) return `Add the ${missing.join(", ")} before continuing.`;
    }
    if (k === "bank") {
      if (!data.bankMethod) return "Choose how you will connect a bank account before continuing.";
      if (data.bankMethod === "statement" && !data.statementImport)
        return "Upload your bank statement CSV before continuing.";
    }
    if (k === "projects") {
      if (Object.keys(data.projectActions).length < assignedProjectCount)
        return "Accept or decline each assigned project before continuing.";
    }
    if (k === "declaration") {
      if (!data.declarationAccepted) return "Acknowledge the compliance declaration before continuing.";
    }
    return null;
  }

  // Map a UI step's typed values to the payload the backend commit expects.
  function payloadForStep(key: string): Record<string, unknown> {
    switch (key) {
      case "organisation":
        return { ...data.organisation };
      case "bank":
        return { bankMethod: data.bankMethod };
      case "projects":
        return { actions: data.projectActions };
      case "declaration":
        return { acknowledged: data.declarationAccepted };
      case "team":
        // Each row → cascade.inviteStaffMember (issues a secure invite link).
        return {
          invites: data.teamInvites.map((m) => ({ email: m.email, role: m.role, name: m.name, subProjectId: m.subProjectId })),
        };
      default:
        return {};
    }
  }

  function advance(sid: number | null) {
    const next = step + 1;
    const far = Math.max(furthest, next);
    setStep(next);
    setFurthest(far);
    setBlocker(null);
    persist(next, data, sid, far);
  }

  async function onContinue() {
    if (busy) return;
    const problem = validateStep(step);
    if (problem) {
      setBlocker(problem);
      return;
    }
    setBlocker(null);

    // Re-visiting a step already committed on the backend (the user went back,
    // then clicked Continue to move forward again). The wizard machine has
    // moved past this step, so re-committing it would be rejected as
    // out-of-order — just move the UI forward without touching the backend.
    if (step < furthest) {
      advance(sessionId);
      return;
    }

    const s = STEPS[step];

    // Welcome: create the backend session (redeem + user + JWT) on first pass,
    // then complete the "welcome" step so the machine advances to org_details.
    if (s.key === "welcome") {
      let sid = sessionId;
      if (!sid) {
        const res = await start(inviteToken as string, {});
        if (!res) {
          setBlocker("We couldn't start your onboarding. Please try again.");
          return;
        }
        sid = res.session.id;
        setSessionId(sid);
      }
      const session = await completeStep(sid, firstStepBackend, {});
      if (!session) {
        setBlocker("We couldn't continue. Please try again.");
        return;
      }
      advance(sid);
      return;
    }

    const sid = sessionId;
    if (!sid) {
      setBlocker("Your session has expired. Return via your invite link to continue.");
      return;
    }

    // Approval gate → finalise the whole wizard (auto-skips subcontractor_invites,
    // marks the session complete) and capture the landing route.
    if (s.key === "gate") {
      const result = await completeOnboarding(sid);
      if (!result) {
        setBlocker("We couldn't submit to the approval gate. Please try again.");
        return;
      }
      if (result.landing) setLanding(result.landing);
      // The onboarding /start session must NOT grant platform access after submit —
      // the org is now awaiting oversight approval (spec §9). Drop the in-memory
      // session so the contractor can't walk into the dashboard; the backend gate
      // (/login + /auth/me) blocks the cookie until their org is approved.
      logout();
      advance(sid);
      return;
    }

    // Steps that commit to a backend catalogue step.
    if (s.backend) {
      const session = await completeStep(sid, s.backend, payloadForStep(s.key));
      if (!session) {
        setBlocker("We couldn't save this step. Please try again.");
        return;
      }
    }
    advance(sid);
  }

  function onBack() {
    setBlocker(null);
    const prev = step - 1;
    setStep(prev);
    persist(prev, data);
  }

  function goToStep(i: number) {
    // The progress rail only enables steps at or before the current one, so this
    // is backward navigation. Committed backend steps are not re-run on the way
    // back; forward progress must go through Continue (which drives the machine).
    if (i > step) return;
    setBlocker(null);
    setStep(i);
    persist(i, data);
  }

  // Loading / invalid / consumed invite gates ------------------------------
  if (peek.isLoading) {
    return <OnboardingSplash icon={<Loader2 className="h-6 w-6 animate-spin" />} title="Loading your invitation" body="One moment while we check your secure invite link." />;
  }
  if (peek.isError || !peek.data) {
    return (
      <OnboardingSplash
        icon={<X className="h-6 w-6" />}
        title="Invitation not found"
        body="We couldn't find this invite link. It may be mistyped or no longer exist. Ask Stratus Health to reissue it."
      />
    );
  }

  // Reporter (tier-4) runs its own lightweight wizard (profile-only, no org/KYC).
  if (peek.data.wizardType === "tier4_reporter") {
    return <ReporterWizard token={inviteToken} peek={peek.data} />;
  }

  const linkState = peek.data.state;
  const canProceed = linkState === "active" || sessionId != null;
  if (!canProceed) {
    const copy =
      linkState === "expired"
        ? "This invitation has expired. Ask Stratus Health to reissue a fresh link."
        : linkState === "revoked"
          ? "This invitation has been revoked. Contact Stratus Health if you believe this is a mistake."
          : "This invitation has already been opened. If it was you, continue on the original device, or ask Stratus Health for a new link.";
    return <OnboardingSplash icon={<ShieldCheck className="h-6 w-6" />} title="This invite can't be opened" body={copy} />;
  }

  // Tier gate: an invite may resolve to a wizard whose UI isn't built yet
  // (returning subcontractor), or to no cascade wizard at all (stratus/funder).
  // Show an honest holding screen instead of running the wrong flow. A live
  // session (sessionId) is always a supported tier, so it bypasses this.
  // (wizardType is derived near the top of the component.)
  if (sessionId == null && (wizardType == null || !SUPPORTED_WIZARDS.has(wizardType))) {
    const body =
      wizardType == null
        ? "This link isn't a contractor onboarding invitation. If you think this is a mistake, contact Stratus Health."
        : "This onboarding type isn't available yet — we're building it. Your invitation is still valid; you'll be able to complete it soon.";
    return <OnboardingSplash icon={<ShieldCheck className="h-6 w-6" />} title="Onboarding not available yet" body={body} />;
  }

  if (resuming && saved != null) {
    return (
      <ResumeLanding
        ws={ws}
        step={saved.step}
        total={STEPS.length}
        onContinue={() => setResuming(false)}
        onRestart={() => {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            /* ignore */
          }
          setStep(0);
          setFurthest(0);
          setData(EMPTY_DATA);
          setBlocker(null);
          setResuming(false);
        }}
      />
    );
  }

  const invite = peek.data.invite;

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center justify-between gap-2 border-b border-line bg-card px-4 sm:px-6">
        <ImpactLedgerLogo />
        <div className="flex items-center gap-3">
          {/* "Step X of Y" is redundant with the WizardProgress bar right
              below this header on the smallest screens, and was the one
              element with no responsive hiding at all in a row that
              already carefully trims everything else (programme name,
              divider) - hiding it here is what keeps the row from crowding
              against the logo. */}
          <span className="hidden text-sm text-mute tabular-nums sm:inline">Step <span className="font-medium text-ink">{step + 1}</span> of {STEPS.length}</span>
          <span className="hidden h-4 w-px bg-line sm:inline" aria-hidden />
          <span className="hidden text-sm font-medium text-ink sm:inline">{invite.programmeName ?? ws.programmeName}</span>
          <Pill variant="neutral">{Math.round(((step + 1) / STEPS.length) * 100)}% complete</Pill>
        </div>
      </header>

      <div className="border-b border-line bg-card px-6 py-3">
        <WizardProgress steps={STEPS} currentIndex={step} onStepClick={goToStep} />
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-8">
          {step === 0 && <Welcome ws={ws} invite={invite} project={assignedProject} onNotMe={() => setForwardOpen(true)} />}
          {step === 1 && (
            <Profile
              invite={invite}
              sessionId={sessionId}
              complete={data.profileComplete}
              onComplete={() => patch({ profileComplete: true })}
              onReset={() => patch({ profileComplete: false })}
            />
          )}
          {step === 2 && <Organisation values={data.organisation} onChange={(organisation) => patch({ organisation })} />}
          {step === 3 && (
            <Bank
              method={data.bankMethod}
              onSelect={(bankMethod) => patch({ bankMethod })}
              sessionId={sessionId}
              statementImport={data.statementImport}
              onStatementImported={(statementImport) => patch({ statementImport })}
            />
          )}
          {step === 4 && <Projects project={assignedProject} actions={data.projectActions} onChange={(projectActions) => patch({ projectActions })} />}
          {step === 5 && <Declaration ws={ws} accepted={data.declarationAccepted} onToggle={(declarationAccepted) => patch({ declarationAccepted })} />}
          {step === 6 && <Team invites={data.teamInvites} onChange={(teamInvites) => patch({ teamInvites })} roleOptions={staffRoleOptions} projectId={invite.projectId} />}
          {step === 7 && <Gate />}
          {step === 8 && <Activation invite={invite} onDone={() => navigate("/login")} />}
        </div>
      </main>

      {step < ACTIVATION_INDEX && (
        <div className="border-t border-line bg-card">
          {blocker && (
            <div className="mx-auto max-w-3xl px-6 pt-3">
              <p role="alert" className="rounded-md border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warn">
                {blocker}
              </p>
            </div>
          )}
          <WizardFooter
            className="border-t-0"
            showBack={step > 0}
            onBack={onBack}
            onContinue={onContinue}
            continueDisabled={busy}
            continueLabel={busy ? "Working…" : step === GATE_INDEX ? "Submit to approval gate" : "Continue"}
            secondary={<Button variant="ghost" onClick={saveLater} disabled={busy}>Save and finish later</Button>}
          />
        </div>
      )}

      <ForwardInviteDialog
        open={forwardOpen}
        onOpenChange={setForwardOpen}
        token={inviteToken}
        programmeName={invite.programmeName ?? ws.programmeName}
      />
    </div>
  );
}

function OnboardingSplash({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center border-b border-line bg-card px-6">
        <ImpactLedgerLogo />
      </header>
      <main className="flex flex-1 items-center justify-center px-6">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-4 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brandSoft text-brand">{icon}</span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
              <p className="mt-1 text-sm text-mute">{body}</p>
            </div>
          </CardBody>
        </Card>
      </main>
    </div>
  );
}

function ResumeLanding({
  ws,
  step,
  total,
  onContinue,
  onRestart,
}: {
  ws: ReturnType<typeof useActiveWorkspace>;
  step: number;
  total: number;
  onContinue: () => void;
  onRestart: () => void;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center justify-between border-b border-line bg-card px-6">
        <ImpactLedgerLogo />
        <span className="text-sm font-medium text-ink">{ws.programmeName}</span>
      </header>
      <main className="flex flex-1 items-center justify-center px-6">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-4 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brandSoft text-brand">
              <RotateCcw className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-ink">Continue where you left off</h1>
              <p className="mt-1 text-sm text-mute">
                You were on step {step + 1} of {total}: {STEPS[step]?.label}. Your saved details are restored. Stratus review continues in the background while you are away.
              </p>
            </div>
            <Button size="lg" className="w-full" onClick={onContinue}>Continue · step {step + 1} of {total}</Button>
            <Button variant="ghost" className="w-full" onClick={onRestart}>Start over</Button>
          </CardBody>
        </Card>
      </main>
    </div>
  );
}

function StepHead({ title, body }: { title: string; body: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
      <p className="mt-1 text-sm text-mute">{body}</p>
    </div>
  );
}

function Welcome({
  ws,
  invite,
  project,
  onNotMe,
}: {
  ws: ReturnType<typeof useActiveWorkspace>;
  invite: OnboardingInvite;
  project: AssignedProject | null;
  onNotMe: () => void;
}) {
  return (
    <div>
      <StepHead title="The programme you are joining" body="Review these details before you begin." />
      <Card>
        <CardBody className="space-y-4">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            {/* The invite's own programme — the active workspace is just
                whatever was last browsed here, and need not be the one this
                invite belongs to. */}
            <Info label="Programme" value={invite.programmeName ?? ws.programmeName} />
            <Info label="Funder" value={ws.funder.name} />
            <Info label="Recipient" value={ws.recipient.name} />
            <Info label="Oversight" value={ws.oversight.name} />
          </dl>
          <div className="rounded-md bg-subtle px-3 py-2 text-xs text-mute">
            Invitation sent to <span className="font-medium text-ink">{invite.email}</span>.
          </div>
          {project && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Your assigned project</p>
              <div className="rounded-md border border-line p-3">
                <p className="text-sm font-medium text-ink">{project.name}</p>
                {project.district && <p className="text-xs text-faint">{project.district}</p>}
              </div>
            </div>
          )}
          <p className="rounded-md bg-subtle px-3 py-2 text-xs text-mute">Compliance pack: {ws.compliancePackName}.</p>
          <button className="text-sm text-brand hover:underline" onClick={onNotMe}>This is not me</button>
        </CardBody>
      </Card>
    </div>
  );
}

const ORG_FIELDS: { key: string; label: string; required: boolean }[] = [
  { key: "legalName", label: "Legal name", required: true },
  { key: "tradingName", label: "Trading name", required: false },
  { key: "registrationId", label: "Registration ID", required: true },
  { key: "taxId", label: "Tax / VAT identifier", required: false },
  { key: "country", label: "Country of incorporation", required: true },
  { key: "primaryContact", label: "Primary contact", required: true },
];

function Organisation({
  values,
  onChange,
}: {
  values: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  return (
    <div>
      <StepHead title="Your organisation" body="Confirm your legal details and primary contact. Fields marked required must be filled to continue." />
      <Card>
        <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ORG_FIELDS.map((f) => (
            <LabeledInput
              key={f.key}
              label={f.label}
              required={f.required}
              value={values[f.key] ?? ""}
              onChange={(v) => onChange({ ...values, [f.key]: v })}
            />
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

// Account step (spec §9 onboarding): the invitee confirms who they are (email +
// role, both read-only, from the invite) and chooses a password. The user row
// was created at /start with a random password; this replaces it so they can log
// in normally later. The password is set via its own action and only a
// completion flag is persisted — the password itself never touches localStorage.
function Profile({
  invite,
  sessionId,
  complete,
  onComplete,
  onReset,
}: {
  invite: OnboardingInvite;
  sessionId: number | null;
  complete: boolean;
  onComplete: () => void;
  onReset: () => void;
}) {
  const { setPassword, isPending, error } = useSetPassword();
  const [password, setPasswordValue] = useState("");
  const [confirm, setConfirm] = useState("");

  const passwordError =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH
      ? `Use at least ${MIN_PASSWORD_LENGTH} characters.`
      : undefined;
  const confirmError =
    confirm.length > 0 && confirm !== password ? "Passwords don't match." : undefined;
  const canSet =
    password.length >= MIN_PASSWORD_LENGTH && confirm === password && sessionId != null;

  async function handleSet() {
    if (!canSet || sessionId == null) return;
    const ok = await setPassword(sessionId, password);
    if (!ok) return;
    setPasswordValue("");
    setConfirm("");
    toast.success("Password set.");
    onComplete();
  }

  return (
    <div>
      <StepHead title="Set up your account" body="Confirm your invitation details and choose a password to secure your account." />

      <Card className="mb-4">
        <CardBody className="space-y-3">
          {invite.name && (
            <div className="flex items-center gap-3">
              <UserPlus className="h-4 w-4 shrink-0 text-mute" aria-hidden />
              <div className="min-w-0">
                <p className="text-xs text-faint">Your name</p>
                <p className="truncate text-sm font-medium text-ink">{invite.name}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 shrink-0 text-mute" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs text-faint">Invited email</p>
              <p className="truncate text-sm font-medium text-ink">{invite.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-4 w-4 shrink-0 text-mute" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs text-faint">Your role</p>
              <p className="text-sm font-medium text-ink">{roleLabel(invite.role)}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {complete ? (
        <Card>
          <CardBody className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 shrink-0 text-ok" aria-hidden />
              <p className="text-sm font-medium text-ink">Password set. You can continue.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onReset}>Change password</Button>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Password" error={passwordError}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </Field>
              <Field label="Confirm password" error={confirmError}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                />
              </Field>
            </div>
            {error && <p className="text-sm text-bad">{error}</p>}
            <div className="flex justify-end">
              <Button onClick={() => void handleSet()} loading={isPending} disabled={!canSet}>
                <Lock className="mr-2 h-4 w-4" aria-hidden /> Set password
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Bank({
  method,
  onSelect,
  sessionId,
  statementImport,
  onStatementImported,
}: {
  method: string | null;
  onSelect: (m: string) => void;
  sessionId: number | null;
  statementImport: StatementImportSummary | null;
  onStatementImported: (s: StatementImportSummary | null) => void;
}) {
  const cards = [
    { key: "open", title: "Open banking", icon: Landmark, body: "Stanbic, DFCU, Centenary, Standard Chartered. Observation-only access.", disabled: true },
    { key: "statement", title: "Bank statement", icon: FileSpreadsheet, body: "Upload a recent statement (CSV export) — transactions are imported for reconciliation.", disabled: false },
    { key: "manual", title: "Manual entry", icon: FileText, body: "Type in your account details for verification by Stratus.", disabled: true },
    { key: "momo", title: "Mobile money", icon: Smartphone, body: "MTN MoMo or Airtel Money, verified with a token payment.", disabled: true },
  ];
  return (
    <div>
      <StepHead title="Connect a bank account" body="Stratus and the funder see transaction observations only. No money moves through Philanthrify." />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {cards.map((c) => (
          <button key={c.key} disabled={c.disabled} onClick={() => { if (c.disabled) return; onSelect(c.key); if (c.key !== "statement") toast.success(c.key === "open" ? "Account verified. Observation-only access granted." : "Connection method selected"); }} className={`rounded-lg border p-4 text-left transition-colors ${c.disabled ? "cursor-not-allowed border-line bg-card opacity-50" : method === c.key ? "border-brand bg-brandSoft" : "border-line bg-card hover:border-strong"}`}>
            <div className="flex items-center justify-between">
              <c.icon className="h-5 w-5 text-brand" />
              {c.disabled ? (
                <span className="rounded-full border border-line bg-subtle px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-mute">
                  Coming soon
                </span>
              ) : (
                method === c.key && <Check className="h-4 w-4 text-ok" />
              )}
            </div>
            <p className="mt-2 text-sm font-medium text-ink">{c.title}</p>
            <p className="mt-1 text-xs text-mute">{c.body}</p>
          </button>
        ))}
      </div>

      {method === "statement" && !statementImport && sessionId != null && (
        <StatementUpload sessionId={sessionId} onImported={onStatementImported} />
      )}
      {method === "statement" && statementImport && (
        <Card className="mt-4">
          <CardBody className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Check className="h-5 w-5 shrink-0 text-ok" aria-hidden />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">
                  {statementImport.fileName} — {statementImport.imported} transaction{statementImport.imported === 1 ? "" : "s"} imported
                </p>
                <p className="text-xs text-mute">
                  {statementImport.deduped > 0 && `${statementImport.deduped} already imported. `}
                  {statementImport.skipped > 0 && `${statementImport.skipped} skipped. `}
                  Pending verification by Stratus Health.
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onStatementImported(null)}>
              Upload another
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Projects({
  project,
  actions,
  onChange,
}: {
  project: AssignedProject | null;
  actions: Record<string, string>;
  onChange: (a: Record<string, string>) => void;
}) {
  if (!project) {
    return (
      <div>
        <StepHead title="Projects" body="This invitation has no project attached." />
        <Card>
          <CardBody>
            <p className="text-sm text-mute">
              Ask whoever invited you to reissue the invitation against a project — there is nothing to accept here.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }
  const count = Object.keys(actions).length;
  const details = [
    project.district,
    project.budgeted != null ? `budget ${Math.round(project.budgeted / 1e9)} billion UGX` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div>
      <StepHead title="Projects" body={`Accept or decline each assigned project. ${count} of 1 projects actioned.`} />
      <div className="space-y-3">
        <Card>
          <CardBody>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-ink">{project.name}</p>
                {details && <p className="text-sm text-faint">{details}</p>}
              </div>
              {actions[project.id] && (
                <Pill variant={actions[project.id] === "Accepted" ? "success" : "neutral"}>{actions[project.id]}</Pill>
              )}
            </div>
            {!actions[project.id] && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => onChange({ ...actions, [project.id]: "Accepted" })}>Accept</Button>
                <Button size="sm" variant="ghost" onClick={() => onChange({ ...actions, [project.id]: "Declined" })}>Decline</Button>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// Clause-21 compliance declaration. MVP: acknowledge only — no document upload.
// The acknowledgement is recorded against project.declarationstatus by the backend.
function Declaration({ ws, accepted, onToggle }: { ws: ReturnType<typeof useActiveWorkspace>; accepted: boolean; onToggle: (v: boolean) => void }) {
  return (
    <div>
      <StepHead title="Compliance declaration" body="Confirm you have read and will operate under the programme's compliance terms before continuing." />
      <Card>
        <CardBody className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-info/30 bg-infoSoft px-4 py-3 text-sm text-info">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              You are joining {ws.programmeName} under {ws.compliancePackName}. By acknowledging, your organisation agrees to the reporting, evidence and spend-approval obligations of the applied compliance pack.
            </span>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-mute">
            <li>Spend above the role-pack threshold is routed for oversight approval.</li>
            <li>Evidence is required before funds are confirmed as spent.</li>
            <li>Subcontractors and staff you add inherit these obligations.</li>
          </ul>
          <label className="flex cursor-pointer items-start gap-3 rounded-card border border-line bg-card p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-brand"
              checked={accepted}
              onChange={(e) => onToggle(e.target.checked)}
            />
            <span className="text-sm text-ink">I acknowledge and accept the compliance declaration on behalf of my organisation.</span>
          </label>
        </CardBody>
      </Card>
      <p className="mt-2 text-xs text-faint">This declaration is recorded against your project's compliance status. No documents are uploaded at this step.</p>
    </div>
  );
}

function Team({ invites, onChange, roleOptions, projectId }: { invites: TeamInvite[]; onChange: (v: TeamInvite[]) => void; roleOptions: { key: string; label: string }[]; projectId: number | null }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(roleOptions[0].key);
  const [subProjectId, setSubProjectId] = useState("");

  // Real sub-projects of the onboarding project, from the backend so the chosen
  // id is one an invite can attach to. Not demo-gated — onboarding always runs
  // against the backend, and the prime's authority over a sub-project comes from
  // leading its parent (cascade honours ancestor-lead authority).
  const { data: subRows } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["onboarding-subprojects", projectId],
    queryFn: () => api.getSubProjects(String(projectId)),
    enabled: projectId != null,
    staleTime: 60_000,
  });
  const subProjects = ((subRows ?? []) as { id: number | string; name?: string; title?: string; slug?: string }[]).map((r) => ({
    id: String(r.id),
    name: r.name ?? r.title ?? r.slug ?? String(r.id),
  }));

  const emailError = email.trim().length > 0 && !isValidEmail(email) ? "Enter a valid email address." : undefined;
  const canAdd = name.trim().length > 0 && isValidEmail(email);

  function reset() {
    setName("");
    setEmail("");
    setRole(roleOptions[0].key);
    setSubProjectId("");
  }

  function addMember() {
    const trimmedEmail = email.trim();
    if (invites.some((i) => i.email.toLowerCase() === trimmedEmail.toLowerCase())) {
      toast.error("That email has already been added.");
      return;
    }
    const roleLabel = roleOptions.find((r) => r.key === role)?.label ?? role;
    const sub = subProjects.find((s) => s.id === subProjectId);
    onChange([...invites, { name: name.trim(), email: trimmedEmail, role, roleLabel, subProjectId: sub?.id, subProjectName: sub?.name }]);
    toast.success(`${name.trim()} will be invited once your onboarding is approved.`);
    reset();
    setOpen(false);
  }

  function removeMember(target: string) {
    onChange(invites.filter((i) => i.email !== target));
  }

  return (
    <div>
      <StepHead title="Team" body="Invite the people who will work on this programme and assign each a role from your project's role pack. Each receives a secure invite link once your onboarding is approved — you can also add more later from your workspace." />
      <Card>
        <CardBody className="space-y-3">
          {invites.length === 0 ? (
            <p className="rounded-md border border-dashed border-line px-3 py-6 text-center text-sm text-faint">
              No team members added yet.
            </p>
          ) : (
            invites.map((m) => (
              <div key={m.email} className="flex items-center justify-between rounded-md border border-line p-3">
                <div>
                  <p className="text-sm font-medium text-ink">{m.name}</p>
                  <p className="text-xs text-faint">{m.email} · {m.roleLabel}{m.subProjectName ? ` · ${m.subProjectName}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Pill variant="warn">Pending invite</Pill>
                  <button
                    aria-label={`Remove ${m.name}`}
                    onClick={() => removeMember(m.email)}
                    className="-my-2 flex h-11 w-11 items-center justify-center rounded-md text-faint hover:bg-subtle hover:text-bad"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
          <Button variant="secondary" onClick={() => setOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" /> Add member
          </Button>
        </CardBody>
      </Card>

      <Dialog.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
            <div className="flex items-start justify-between gap-3 p-6 pb-3">
              <Dialog.Title className="text-base font-semibold tracking-tight text-ink">Add a team member</Dialog.Title>
              <Dialog.Close asChild>
                <button aria-label="Close" className="-m-2 flex h-11 w-11 items-center justify-center rounded-md text-faint hover:bg-subtle hover:text-ink">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                They receive a secure invite link to join the programme and complete their own setup.
              </Dialog.Description>
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Daniel Mukasa" />
              </Field>
              <Field label="Email" error={emailError}>
                <Input value={email} type="email" onChange={(e) => setEmail(e.target.value)} placeholder="daniel@nextgen.co.ug" />
              </Field>
              {subProjects.length > 0 && (
                <Field label="Sub-project (optional)">
                  <Select value={subProjectId} onChange={(e) => setSubProjectId(e.target.value)}>
                    <option value="">Whole project</option>
                    {subProjects.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="Role">
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                  {roleOptions.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canAdd} onClick={addMember}>Add member</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function Gate() {
  // MVP: KYC is deferred, so it is not shown here. The gate represents the fact
  // that Stratus oversight must approve the organisation before it is fully
  // activated (org sits at awaiting_approval_gate until then). These states are
  // still illustrative copy — wiring them to the org's live cascade status is a
  // follow-up.
  const blockers = [
    { label: "Organisation details submitted", state: "Complete", tone: "success" as const },
    { label: "Projects actioned", state: "Complete", tone: "success" as const },
    { label: "Stratus onboarding approval", state: "Pending Stratus review", tone: "warn" as const },
  ];
  return (
    <div>
      <StepHead title="Approval gate" body="You have submitted everything. Stratus Health now reviews your onboarding; your organisation is activated once approved." />
      <div className="space-y-2">
        {blockers.map((b) => (
          <div key={b.label} className="flex items-center justify-between rounded-card border border-line bg-card p-3">
            <span className="text-sm text-ink">{b.label}</span>
            <Pill variant={b.tone}>{b.state}</Pill>
          </div>
        ))}
      </div>
    </div>
  );
}

function Activation({ invite, onDone }: { invite: OnboardingInvite; onDone: () => void }) {
  return (
    <div className="flex flex-col items-center py-10 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brandSoft text-brand"><ShieldCheck className="h-8 w-8" /></span>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-ink">Submitted for approval</h1>
      <p className="mt-1 max-w-md text-sm text-mute">
        Your organisation, bank connection, team and projects have been submitted. You can't sign in to your workspace yet — Stratus Health oversight must review and approve your onboarding first.
      </p>
      <p className="mt-3 max-w-md text-sm text-mute">
        We'll email you at <span className="font-medium text-ink">{invite.email}</span> once you're approved. Please wait for that notification before trying to log in.
      </p>
      {(invite.inviterName || invite.inviterEmail) && (
        <p className="mt-3 max-w-md text-sm text-mute">
          Questions in the meantime? Contact{invite.inviterName ? <> {invite.inviterName}</> : <> whoever invited you</>}
          {invite.inviterEmail && (
            <> at <a className="font-medium text-brand hover:underline" href={`mailto:${invite.inviterEmail}`}>{invite.inviterEmail}</a></>
          )}
          .
        </p>
      )}
      <Button variant="secondary" className="mt-6" onClick={onDone}>Back to sign in</Button>
    </div>
  );
}

// "This is not me" resolves to a real, visible result: the recipient forwards the invite to
// the right person and gets an on-screen confirmation, rather than a debug toast.
function ForwardInviteDialog({
  open,
  onOpenChange,
  token,
  programmeName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string | undefined;
  programmeName: string;
}) {
  const { forward, isPending, error } = useForwardInvite();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<{ name: string; email: string } | null>(null);

  const emailError = email.trim().length > 0 && !isValidEmail(email) ? "Enter a valid email address." : undefined;
  const canSend = name.trim().length > 0 && isValidEmail(email);

  function reset() {
    setName("");
    setEmail("");
    setSent(null);
  }

  async function onForward() {
    if (!token) return;
    const ok = await forward(token, { email: email.trim(), name: name.trim() });
    if (ok) setSent({ name: name.trim(), email: email.trim() });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {sent != null ? "Invitation forwarded" : "Forward this invitation"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="-m-2 flex h-11 w-11 items-center justify-center rounded-md text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {sent != null ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  A secure invite to the {programmeName} onboarding has been sent to {sent.name} at {sent.email}. This invite link is now closed; the new recipient continues setup.
                </span>
              </div>
              <p className="text-sm text-mute">You can close this window. No further action is needed from you.</p>
              <div className="flex justify-end">
                <Button onClick={() => onOpenChange(false)}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Pass this onboarding to the right person in your organisation. They receive a fresh secure invite and complete setup; your link is then closed.
              </Dialog.Description>
              <Field label="Their name">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Grace Nansubuga"
                />
              </Field>
              <Field label="Their email" error={emailError}>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="director@nextgen.co.ug"
                />
              </Field>
              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {sent == null && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSend || isPending} onClick={onForward}>{isPending ? "Forwarding…" : "Forward invitation"}</Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs text-faint">{label}</dt><dd className="text-sm font-medium text-ink">{value}</dd></div>;
}

function LabeledInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <Field label={label} required={required}>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}
